import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { EMPTY, finalize, Observable, tap } from 'rxjs';

import { AuthRequest, AuthSession, PasscodeReference, PasscodeRequest } from '../api/models/auth.model';
import { AuthEndpoint } from '../api/services/auth-endpoint';
import { PortalAuthEndpoint } from '../api/services/portal-auth-endpoint';
import { UserState } from '../state/user-state';

const TOKEN_KEY = 'auth_token';

const POSTHOG_HOSTS = [
  'participations.equalseat.com',
  'participation-portal-frontend.staging.equalseat.com',
];

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly authApi = inject(AuthEndpoint);
  private readonly portalAuthApi = inject(PortalAuthEndpoint);
  private readonly userState = inject(UserState);

  readonly isAuthenticated = signal(!!localStorage.getItem(TOKEN_KEY));

  readonly reference = signal<string | null>(null);
  readonly challengeEmail = signal<string | null>(null);

  private credentials: PasscodeRequest | null = null;

  constructor() {
    const token = this.getToken();

    if (token) {
      this.userState.setFromToken(token);
    }
  }

  requestPasscode(credentials: PasscodeRequest): Observable<PasscodeReference> {
    return this.authApi.requestPasscode(credentials).pipe(
      tap(res => {
        this.credentials = credentials;
        this.reference.set(res.reference);
        this.challengeEmail.set(credentials.email);
      }),
    );
  }

  verifyPasscode(passcode: string): Observable<AuthSession> {
    const body: AuthRequest = {
      email: this.credentials?.email ?? '',
      password: this.credentials?.password ?? '',
      reference: this.reference() ?? '',
      passcode,
    };

    return this.authApi.authenticate(body).pipe(tap(res => this.setSession(res)));
  }

  resendPasscode(): Observable<PasscodeReference> {
    return this.credentials ? this.requestPasscode(this.credentials) : EMPTY;
  }

  logout(): void {
    this.portalAuthApi
      .logout()
      .pipe(finalize(() => this.clearSession()))
      .subscribe({ error: () => { } });
  }

  clearSession(): void {
    localStorage.removeItem(TOKEN_KEY);
    this.userState.clear();
    this.resetAnalytics();
    this.isAuthenticated.set(false);
    this.clearChallenge();
    this.router.navigate(['/account/sign-in']);
  }

  clearChallenge(): void {
    this.reference.set(null);
    this.challengeEmail.set(null);
    this.credentials = null;
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  private resetAnalytics(): void {
    const isPosthogHost = POSTHOG_HOSTS.includes(window.location.host);
    if (!isPosthogHost) return;

    const posthog = (window as any)['posthog'];
    if (posthog && typeof posthog.reset === 'function') {
      posthog.reset();
    }
  }

  private setSession(session: AuthSession): void {
    localStorage.setItem(TOKEN_KEY, session.token);
    this.userState.setFromToken(session.token);
    this.isAuthenticated.set(true);
    this.clearChallenge();
  }
}
